/**
 * The parts tray. Pull a part out of the tray onto the board to place it.
 * On phones the tray sits at the bottom and scrolls sideways: pull UP to grab a part.
 * On wide screens it sits on the left and scrolls up/down: pull RIGHT to grab.
 */
import type { PartProps, PartType } from '../core/types';
import { h } from './dom';

export interface TrayEntry {
  key: string;
  type: PartType;
  props: PartProps;
  label: string;
  /** Remaining (undefined = unlimited). */
  left?: number;
}

export class Tray {
  readonly el: HTMLElement;
  private items = new Map<string, HTMLElement>();
  private entries: TrayEntry[] = [];
  layout: 'left' | 'bottom' = 'left';

  constructor(
    parent: HTMLElement,
    private thumbs: Map<string, string>,
    private onGrab: (entry: TrayEntry, e: PointerEvent) => void,
    private onTap: (entry: TrayEntry) => void,
  ) {
    this.el = h('div.tray.left', { role: 'toolbar', 'aria-label': 'Parts' });
    parent.append(this.el);
  }

  setLayout(layout: 'left' | 'bottom') {
    this.layout = layout;
    this.el.className = `tray ${layout}`;
  }

  set(entries: TrayEntry[]) {
    this.entries = entries;
    this.el.replaceChildren();
    this.items.clear();
    for (const entry of entries) {
      const img = this.thumbs.get(entry.key);
      const item = h(
        'div.tray-item',
        {
          role: 'button',
          tabindex: 0,
          'aria-label': `${entry.label}. Drag onto the board.`,
          'data-key': entry.key,
        },
        img
          ? h('img', { src: img, alt: '', draggable: 'false' })
          : h('span', { style: { fontSize: '30px' } }, '🔹'),
        h('span.label', {}, entry.label),
        entry.left !== undefined ? h('span.count', {}, String(entry.left)) : null,
      );
      if (entry.left === 0) item.classList.add('empty');
      this.attach(item, entry);
      this.items.set(entry.key, item);
      this.el.append(item);
    }
  }

  updateCounts(left: Map<string, number | undefined>) {
    for (const entry of this.entries) {
      const item = this.items.get(entry.key);
      if (!item) continue;
      const n = left.get(entry.key);
      entry.left = n;
      const badge = item.querySelector('.count');
      if (badge) badge.textContent = String(n ?? '');
      item.classList.toggle('empty', n === 0);
    }
  }

  wiggle(key: string) {
    const item = this.items.get(key);
    if (!item) return;
    item.classList.remove('wiggle');
    void item.offsetWidth;
    item.classList.add('wiggle');
  }

  private attach(item: HTMLElement, entry: TrayEntry) {
    let start: { x: number; y: number; id: number; touch: boolean } | null = null;
    let grabbed = false;
    item.addEventListener('pointerdown', (e) => {
      if (entry.left === 0) return;
      start = { x: e.clientX, y: e.clientY, id: e.pointerId, touch: e.pointerType !== 'mouse' };
      grabbed = false;
      if (!start.touch) e.preventDefault();
    });
    item.addEventListener('pointermove', (e) => {
      if (!start || grabbed || e.pointerId !== start.id) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const dist = Math.hypot(dx, dy);
      const go = !start.touch
        ? dist > 5
        : this.layout === 'bottom'
          ? -dy > 10 && Math.abs(dy) > Math.abs(dx) * 0.8
          : dx > 10 && Math.abs(dx) > Math.abs(dy) * 0.8;
      if (go) {
        grabbed = true;
        try {
          item.releasePointerCapture(e.pointerId);
        } catch {
          /* fine */
        }
        this.onGrab(entry, e);
      }
    });
    const end = (e: PointerEvent) => {
      if (!start || e.pointerId !== start.id) return;
      const wasTap = !grabbed && Math.hypot(e.clientX - start.x, e.clientY - start.y) < 8;
      start = null;
      if (wasTap && e.type === 'pointerup') this.onTap(entry);
    };
    item.addEventListener('pointerup', end);
    item.addEventListener('pointercancel', end);
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') this.onTap(entry);
    });
  }
}
